'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Inject,
  Page,
  Resize,
  Sort,
} from '@syncfusion/ej2-react-grids';
import { useAppColors } from '@/styles/colors';
import { resolveDividendStatus } from '@/libs/dividend-status';

type DividendRow = {
  id: string;
  eventType: string | null;
  sourceEventType: string | null;
  comDate: string | null;
  payDate: string | null;
  competence: string | null;
  amountPerShareRaw: number | null;
  amountPerShareAdjusted: number | null;
  priceAtReference: number | null;
  dividendYieldPercent: number | null;
  isInDy12mWindow: boolean;
};

type TickerDividendsTabClientProps = {
  tickerLabel: string;
  dividends: DividendRow[];
  dyWindow: {
    fromDate: string | null;
    asOfDate: string | null;
  };
};

type RangeId = '6M' | '1A' | '3A' | '5A' | 'TUDO';

const DAY_MS = 24 * 60 * 60 * 1000;
const PLOT_VERTICAL_PADDING_RATIO = 0.08;

const RANGE_CONFIG: Array<{ id: RangeId; label: string; days: number | null }> = [
  { id: '6M', label: '6M', days: 180 },
  { id: '1A', label: '1A', days: 365 },
  { id: '3A', label: '3A', days: 365 * 3 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const getTodayKeyInBrt = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const resolveReferenceDate = (row: DividendRow) => row.payDate ?? row.comDate ?? row.competence;

export function TickerDividendsTabClient({ tickerLabel, dividends, dyWindow }: TickerDividendsTabClientProps) {
  const colors = useAppColors();
  const dividendsColors = colors.modules.dividends;
  const rangeColors = colors.actions.range;
  const whiteColor = colors.semantic.white;
  const [range, setRange] = useState<RangeId>('1A');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showAverage, setShowAverage] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showDy, setShowDy] = useState(true);
  const todayKey = getTodayKeyInBrt();
  const todayTs = new Date(`${todayKey}T00:00:00Z`).getTime();
  const selectedRange = RANGE_CONFIG.find((item) => item.id === range) ?? RANGE_CONFIG[1];

  const dividendRows = useMemo(() => dividends.filter((row) => row.sourceEventType === 'dividend'), [dividends]);
  const otherRows = useMemo(() => dividends.filter((row) => row.sourceEventType !== 'dividend'), [dividends]);

  const chartSeries = useMemo(() => {
    const normalizedEvents = dividendRows
      .map((row) => {
        const referenceDate = resolveReferenceDate(row);
        if (!referenceDate || row.amountPerShareAdjusted === null) return null;
        const ts = new Date(referenceDate).getTime();
        if (!Number.isFinite(ts)) return null;
        return {
          id: row.id,
          ts,
          date: referenceDate,
          amount: row.amountPerShareAdjusted,
          priceAtReference: row.priceAtReference,
          dividendYieldPercent: row.dividendYieldPercent,
        };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          ts: number;
          date: string;
          amount: number;
          priceAtReference: number | null;
          dividendYieldPercent: number | null;
        } => item !== null,
      )
      .sort((a, b) => a.ts - b.ts);

    if (normalizedEvents.length === 0) return normalizedEvents;

    const thresholdTs = selectedRange.days === null ? Number.NEGATIVE_INFINITY : todayTs - selectedRange.days * DAY_MS;
    const eventsInRange = normalizedEvents.filter((item) => item.ts >= thresholdTs && item.ts <= todayTs);

    const groupedByMonth = new Map<
      string,
      {
        amount: number;
        ts: number;
        date: string;
        ids: string[];
        priceAtReference: number | null;
        dividendYieldPercent: number;
      }
    >();
    for (const event of eventsInRange) {
      const monthKey = event.date.slice(0, 7);
      const current = groupedByMonth.get(monthKey);
      if (!current) {
        groupedByMonth.set(monthKey, {
          amount: event.amount,
          ts: event.ts,
          date: event.date,
          ids: [event.id],
          priceAtReference: event.priceAtReference,
          dividendYieldPercent: event.dividendYieldPercent ?? 0,
        });
        continue;
      }
      current.amount += event.amount;
      current.ids.push(event.id);
      if (event.dividendYieldPercent !== null) {
        current.dividendYieldPercent += event.dividendYieldPercent;
      }
      if (event.ts > current.ts) {
        current.ts = event.ts;
        current.date = event.date;
        current.priceAtReference = event.priceAtReference;
      }
    }

    return Array.from(groupedByMonth.entries())
      .map(([monthKey, value]) => ({
        id: `month-${monthKey}`,
        ts: value.ts,
        date: value.date,
        amount: value.amount,
        priceAtReference: value.priceAtReference,
        dividendYieldPercent: value.dividendYieldPercent,
      }))
      .sort((a, b) => a.ts - b.ts);
  }, [dividendRows, selectedRange.days, todayTs]);
  const windowStartTs = useMemo(() => {
    if (selectedRange.days !== null) return todayTs - selectedRange.days * DAY_MS;
    return chartSeries.length > 0 ? chartSeries[0].ts : todayTs;
  }, [chartSeries, selectedRange.days, todayTs]);
  const windowStartDateKey = useMemo(() => new Date(windowStartTs).toISOString().slice(0, 10), [windowStartTs]);
  const mapRowsToTable = (rows: DividendRow[]) =>
    [...rows]
      .sort((a, b) => {
        const aTs = new Date(resolveReferenceDate(a) ?? 0).getTime();
        const bTs = new Date(resolveReferenceDate(b) ?? 0).getTime();
        return bTs - aTs;
      })
      .map((row) => ({
        id: row.id,
        eventType: row.eventType ?? '-',
        comDate: formatDate(row.comDate),
        payDate: formatDate(row.payDate),
        competence: formatDate(row.competence),
        amountPerShareRaw: row.amountPerShareRaw === null ? '-' : BRL_FORMATTER.format(row.amountPerShareRaw),
        amountPerShareAdjusted:
          row.amountPerShareAdjusted === null ? '-' : BRL_FORMATTER.format(row.amountPerShareAdjusted),
        priceAtReference: row.priceAtReference === null ? '-' : BRL_FORMATTER.format(row.priceAtReference),
        dividendYieldPercent:
          row.dividendYieldPercent === null ? '-' : PERCENT_FORMATTER.format(row.dividendYieldPercent / 100),
        inDy12mWindow: row.isInDy12mWindow ? 'Sim' : 'Não',
        status: resolveDividendStatus(row.payDate, todayKey),
      }));

  const tableRows = useMemo(() => mapRowsToTable(dividendRows), [dividendRows, todayKey]);
  const otherTableRows = useMemo(() => mapRowsToTable(otherRows), [otherRows, todayKey]);

  const totalPeriod = useMemo(() => chartSeries.reduce((acc, item) => acc + item.amount, 0), [chartSeries]);
  const averageAmount = useMemo(
    () => (chartSeries.length > 0 ? totalPeriod / chartSeries.length : 0),
    [chartSeries.length, totalPeriod],
  );

  const width = 1000;
  const height = 340;
  const chartLeft = 52;
  const chartRight = width - 20;
  const chartTop = 20;
  const chartBottom = height - 44;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;
  const plotVerticalPadding = chartHeight * PLOT_VERTICAL_PADDING_RATIO;
  const plotTop = chartTop + plotVerticalPadding;
  const plotBottom = chartBottom - plotVerticalPadding;
  const plotHeight = Math.max(1, plotBottom - plotTop);
  const maxAmount = Math.max(...chartSeries.map((item) => item.amount), 0.000001);
  const maxAmountWithHeadroom = maxAmount * 1.08;
  const chartStartTs = windowStartTs;
  const chartEndTs = todayTs;
  const chartTs = chartSeries.map((item) => item.ts);
  const minGapTs =
    chartTs.length > 1
      ? chartTs.slice(1).reduce((min, ts, index) => Math.min(min, ts - chartTs[index]), Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
  const edgePaddingTs =
    Number.isFinite(minGapTs) && minGapTs > 0 ? Math.max(DAY_MS, Math.floor(minGapTs / 2)) : DAY_MS * 2;
  const chartStartTsPadded = chartStartTs - edgePaddingTs;
  const chartEndTsPadded = chartEndTs + edgePaddingTs;
  const dyWindowRange = useMemo(() => {
    if (!dyWindow.fromDate || !dyWindow.asOfDate || chartSeries.length === 0) return null;
    const fromTs = new Date(`${dyWindow.fromDate}T00:00:00Z`).getTime();
    const asOfTs = new Date(`${dyWindow.asOfDate}T00:00:00Z`).getTime();
    if (!Number.isFinite(fromTs) || !Number.isFinite(asOfTs)) return null;
    return {
      fromTs: Math.min(fromTs, asOfTs),
      asOfTs: Math.max(fromTs, asOfTs),
    };
  }, [dyWindow.asOfDate, dyWindow.fromDate, chartSeries.length]);
  const chartSeriesWithMetrics = useMemo(
    () =>
      chartSeries.map((item) => {
        const price = item.priceAtReference;
        const dy = Number.isFinite(item.dividendYieldPercent) ? item.dividendYieldPercent : null;
        return { ...item, price, priceTs: item.ts, dy };
      }),
    [chartSeries],
  );
  const chartPriceMonthlySeries = useMemo(
    () =>
      chartSeriesWithMetrics
        .filter(
          (item): item is typeof item & { price: number; priceTs: number } =>
            item.price !== null && item.priceTs !== null,
        )
        .map((item) => ({ ts: item.priceTs, close: item.price })),
    [chartSeriesWithMetrics],
  );
  const priceValues = chartPriceMonthlySeries
    .map((item) => item.close)
    .filter((value): value is number => Number.isFinite(value));
  const dyValues = chartSeriesWithMetrics
    .map((item) => item.dy)
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const minPrice = priceValues.length > 0 ? Math.min(...priceValues) : 0;
  const maxPrice = priceValues.length > 0 ? Math.max(...priceValues) : 0;
  const minDy = dyValues.length > 0 ? Math.min(...dyValues) : 0;
  const maxDy = dyValues.length > 0 ? Math.max(...dyValues) : 0;
  const mapTsToX = (ts: number) => {
    if (chartEndTsPadded === chartStartTsPadded) return chartLeft + chartWidth / 2;
    return chartLeft + ((ts - chartStartTsPadded) / (chartEndTsPadded - chartStartTsPadded)) * chartWidth;
  };
  const xCenters = chartSeriesWithMetrics.map((item) => mapTsToX(item.ts));
  const minGap =
    xCenters.length > 1
      ? xCenters.slice(1).reduce((min, x, index) => Math.min(min, x - xCenters[index]), Number.POSITIVE_INFINITY)
      : chartWidth;
  const barWidth = Math.max(3, Math.min(24, Number.isFinite(minGap) ? minGap * 0.65 : 12));
  const clampBarCenter = (xCenter: number) =>
    Math.max(chartLeft + barWidth / 2 + 1, Math.min(chartRight - barWidth / 2 - 1, xCenter));
  const scaleToY = (value: number, min: number, max: number) => {
    if (!Number.isFinite(value)) return plotBottom;
    if (max - min < 0.000001) return plotTop + plotHeight / 2;
    return plotBottom - ((value - min) / (max - min)) * plotHeight;
  };
  const pricePoints = chartPriceMonthlySeries
    .map((item) => {
      const x = mapTsToX(item.ts);
      const y = scaleToY(item.close, minPrice, maxPrice);
      return `${x},${y}`;
    })
    .filter((point): point is string => point !== null);
  const dyPoints = chartSeriesWithMetrics
    .map((item) => {
      if (item.dy === null) return null;
      const x = mapTsToX(item.ts);
      const y = scaleToY(item.dy, minDy, maxDy);
      return `${x},${y}`;
    })
    .filter((point): point is string => point !== null);

  const hoveredItem =
    hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < chartSeriesWithMetrics.length
      ? chartSeriesWithMetrics[hoveredIndex]
      : null;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography variant='h6' fontWeight={700}>
            Dividendos de {tickerLabel}
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {RANGE_CONFIG.map((item) => (
              <Button
                key={item.id}
                size='small'
                variant={range === item.id ? 'contained' : 'outlined'}
                onClick={() => setRange(item.id)}
                sx={
                  range === item.id
                    ? {
                        bgcolor: rangeColors.selectedBg,
                        borderColor: rangeColors.selectedBorder,
                        color: rangeColors.selectedText,
                        '&:hover': {
                          bgcolor: rangeColors.selectedHoverBg,
                          borderColor: rangeColors.selectedHoverBorder,
                        },
                      }
                    : {
                        color: rangeColors.unselectedText,
                        borderColor: rangeColors.unselectedBorder,
                        '&:hover': {
                          color: rangeColors.unselectedHoverText,
                          borderColor: rangeColors.unselectedHoverBorder,
                          backgroundColor: rangeColors.unselectedHoverBg,
                        },
                      }
                }
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Total no período: {BRL_FORMATTER.format(totalPeriod)} por cota
        </Typography>
        <Stack direction='row' spacing={1} sx={{ mt: 1 }} flexWrap='wrap' useFlexGap>
          <SeriesToggle
            active={showAverage}
            color={dividendsColors.average}
            whiteColor={whiteColor}
            label='Média'
            onClick={() => setShowAverage((value) => !value)}
          />
          <SeriesToggle
            active={showPrice}
            color={dividendsColors.price}
            whiteColor={whiteColor}
            label='Preço da cota'
            onClick={() => setShowPrice((value) => !value)}
          />
          <SeriesToggle
            active={showDy}
            color={dividendsColors.dy}
            whiteColor={whiteColor}
            label='DY do evento'
            onClick={() => setShowDy((value) => !value)}
          />
        </Stack>

        {chartSeries.length > 0 ? (
          <Box sx={{ width: '100%', mt: 2 }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width='100%'
              role='img'
              onMouseLeave={() => setHoveredIndex(null)}
              onMouseMove={(event) => {
                if (chartSeriesWithMetrics.length === 0) return;
                const rect = event.currentTarget.getBoundingClientRect();
                if (rect.width <= 0 || chartWidth <= 0) return;
                const relativeX = ((event.clientX - rect.left) / rect.width) * width;
                const clampedX = Math.max(chartLeft, Math.min(chartRight, relativeX));
                let closestIndex = 0;
                let closestDistance = Number.POSITIVE_INFINITY;
                for (let index = 0; index < chartSeriesWithMetrics.length; index += 1) {
                  const x = mapTsToX(chartSeriesWithMetrics[index].ts);
                  const distance = Math.abs(x - clampedX);
                  if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                  }
                }
                setHoveredIndex(closestIndex);
              }}
            >
              <title>{`Gráfico de dividendos de ${tickerLabel}`}</title>
              <rect
                x={chartLeft}
                y={chartTop}
                width={chartWidth}
                height={chartHeight}
                fill='transparent'
                stroke='hsl(var(--border))'
              />
              {dyWindowRange ? (
                <>
                  <rect
                    x={Math.max(chartLeft, mapTsToX(dyWindowRange.fromTs))}
                    y={chartTop}
                    width={Math.max(
                      0,
                      Math.min(chartRight, mapTsToX(dyWindowRange.asOfTs)) -
                        Math.max(chartLeft, mapTsToX(dyWindowRange.fromTs)),
                    )}
                    height={chartHeight}
                    fill={`${dividendsColors.dy}17`}
                  />
                  <line
                    x1={Math.max(chartLeft, Math.min(chartRight, mapTsToX(dyWindowRange.fromTs)))}
                    y1={chartTop}
                    x2={Math.max(chartLeft, Math.min(chartRight, mapTsToX(dyWindowRange.fromTs)))}
                    y2={chartBottom}
                    stroke={dividendsColors.dy}
                    strokeDasharray='4 3'
                    strokeWidth='1'
                    opacity='0.9'
                  />
                  <line
                    x1={Math.max(chartLeft, Math.min(chartRight, mapTsToX(dyWindowRange.asOfTs)))}
                    y1={chartTop}
                    x2={Math.max(chartLeft, Math.min(chartRight, mapTsToX(dyWindowRange.asOfTs)))}
                    y2={chartBottom}
                    stroke={dividendsColors.dy}
                    strokeDasharray='4 3'
                    strokeWidth='1'
                    opacity='0.9'
                  />
                  <text
                    x={Math.max(chartLeft + 4, Math.min(chartRight - 120, mapTsToX(dyWindowRange.fromTs) + 4))}
                    y={chartTop + 14}
                    fontSize='11'
                    fill={dividendsColors.dyGuide}
                    fontWeight='700'
                  >
                    Janela DY 12M
                  </text>
                </>
              ) : null}

              {chartSeriesWithMetrics.map((item, index) => {
                const xCenter = clampBarCenter(mapTsToX(item.ts));
                const barHeight = (item.amount / maxAmountWithHeadroom) * plotHeight;
                const y = plotBottom - barHeight;
                return (
                  <rect
                    key={item.id}
                    x={xCenter - barWidth / 2}
                    y={y}
                    width={barWidth}
                    height={Math.max(1, barHeight)}
                    fill={hoveredIndex === index ? dividendsColors.eventMarkerHover : dividendsColors.eventMarker}
                    rx='2'
                  />
                );
              })}
              {showAverage ? (
                <>
                  <line
                    x1={chartLeft}
                    y1={plotBottom - (averageAmount / maxAmountWithHeadroom) * plotHeight}
                    x2={chartRight}
                    y2={plotBottom - (averageAmount / maxAmountWithHeadroom) * plotHeight}
                    stroke={dividendsColors.average}
                    strokeWidth='1.5'
                    strokeDasharray='5 4'
                  />
                  <text
                    x={chartRight - 4}
                    y={plotBottom - (averageAmount / maxAmountWithHeadroom) * plotHeight - 6}
                    textAnchor='end'
                    fontSize='11'
                    fill={dividendsColors.corporateEventMarker}
                  >
                    {`Média: ${BRL_FORMATTER.format(averageAmount)}`}
                  </text>
                </>
              ) : null}
              {showPrice && pricePoints.length >= 2 ? (
                <polyline
                  points={pricePoints.join(' ')}
                  fill='none'
                  stroke={dividendsColors.price}
                  strokeWidth='2'
                  strokeDasharray='5 4'
                />
              ) : null}
              {showDy && dyPoints.length >= 2 ? (
                <polyline
                  points={dyPoints.join(' ')}
                  fill='none'
                  stroke={dividendsColors.dy}
                  strokeWidth='2'
                  strokeDasharray='4 3'
                />
              ) : null}
              <line x1={chartLeft} y1={chartBottom} x2={chartRight} y2={chartBottom} stroke='hsl(var(--border))' />

              <text x={chartLeft} y={height - 16} fontSize='12' fill='hsl(var(--muted-foreground))'>
                {DATE_FORMATTER.format(new Date(`${windowStartDateKey}T00:00:00Z`))}
              </text>
              <text x={chartRight} y={height - 16} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {DATE_FORMATTER.format(new Date(`${todayKey}T00:00:00Z`))}
              </text>

              {hoveredItem ? (
                <g
                  transform={`translate(${Math.min(
                    chartRight - 170,
                    Math.max(chartLeft + 8, mapTsToX(hoveredItem.ts) + 10),
                  )}, ${chartTop + 8})`}
                >
                  <rect width='160' height='78' rx='8' fill='hsl(var(--card))' stroke='hsl(var(--border))' />
                  <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                    {DATE_FORMATTER.format(new Date(hoveredItem.date))}
                  </text>
                  <text x='10' y='35' fontSize='13' fontWeight='700' fill='hsl(var(--foreground))'>
                    {BRL_FORMATTER.format(hoveredItem.amount)}
                  </text>
                  <text x='10' y='52' fontSize='12' fill={dividendsColors.price} opacity={showPrice ? 1 : 0.45}>
                    {`Cota: ${hoveredItem.price === null ? '-' : BRL_FORMATTER.format(hoveredItem.price)}`}
                  </text>
                  <text x='10' y='68' fontSize='12' fill={dividendsColors.dy} opacity={showDy ? 1 : 0.45}>
                    {`DY evento: ${hoveredItem.dy === null ? '-' : PERCENT_FORMATTER.format(hoveredItem.dy / 100)}`}
                  </text>
                </g>
              ) : null}
            </svg>
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Sem valores de dividendos no intervalo selecionado.
          </Typography>
        )}
      </Paper>

      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Typography variant='h6' fontWeight={700} sx={{ mb: 1.5 }}>
          Histórico completo de proventos
        </Typography>
        <GridComponent
          dataSource={tableRows}
          allowPaging
          allowSorting
          allowFiltering
          allowResizing
          pageSettings={{ pageSize: 20, pageSizes: [20, 50, 100] }}
          filterSettings={{ type: 'Menu' }}
          resizeSettings={{ mode: 'Auto' }}
          width='100%'
          locale='pt'
        >
          <ColumnsDirective>
            <ColumnDirective field='eventType' headerText='Tipo' width='130' minWidth='120' />
            <ColumnDirective field='comDate' headerText='DATA-COM' width='120' minWidth='110' />
            <ColumnDirective field='payDate' headerText='Data pagamento' width='140' minWidth='120' />
            <ColumnDirective field='competence' headerText='Competência' width='120' minWidth='110' />
            <ColumnDirective field='amountPerShareRaw' headerText='Valor bruto' width='140' minWidth='120' />
            <ColumnDirective field='amountPerShareAdjusted' headerText='Valor ajustado' width='130' minWidth='120' />
            <ColumnDirective field='priceAtReference' headerText='Preço cota (ref)' width='150' minWidth='130' />
            <ColumnDirective field='dividendYieldPercent' headerText='DY evento (ref)' width='110' minWidth='90' />
            <ColumnDirective field='inDy12mWindow' headerText='No DY 12M' width='110' minWidth='90' />
            <ColumnDirective field='status' headerText='Status' width='120' minWidth='100' />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, Resize]} />
        </GridComponent>
      </Paper>
      {otherTableRows.length > 0 ? (
        <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
          <Typography variant='h6' fontWeight={700} sx={{ mb: 1.5 }}>
            Outros proventos (não entram no DY)
          </Typography>
          <GridComponent
            dataSource={otherTableRows}
            allowPaging
            allowSorting
            allowFiltering
            allowResizing
            pageSettings={{ pageSize: 20, pageSizes: [20, 50, 100] }}
            filterSettings={{ type: 'Menu' }}
            resizeSettings={{ mode: 'Auto' }}
            width='100%'
            locale='pt'
          >
            <ColumnsDirective>
              <ColumnDirective field='eventType' headerText='Tipo' width='130' minWidth='120' />
              <ColumnDirective field='comDate' headerText='DATA-COM' width='120' minWidth='110' />
              <ColumnDirective field='payDate' headerText='Data pagamento' width='140' minWidth='120' />
              <ColumnDirective field='competence' headerText='Competência' width='120' minWidth='110' />
              <ColumnDirective field='amountPerShareRaw' headerText='Valor bruto' width='140' minWidth='120' />
              <ColumnDirective field='amountPerShareAdjusted' headerText='Valor ajustado' width='130' minWidth='120' />
              <ColumnDirective field='priceAtReference' headerText='Preço cota (ref)' width='150' minWidth='130' />
              <ColumnDirective field='dividendYieldPercent' headerText='DY evento (ref)' width='110' minWidth='90' />
              <ColumnDirective field='inDy12mWindow' headerText='No DY 12M' width='110' minWidth='90' />
              <ColumnDirective field='status' headerText='Status' width='120' minWidth='100' />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Resize]} />
          </GridComponent>
        </Paper>
      ) : null}
    </Stack>
  );
}

function formatDate(value: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return DATE_FORMATTER.format(date);
}

function SeriesToggle({
  active,
  color,
  whiteColor,
  label,
  onClick,
}: {
  active: boolean;
  color: string;
  whiteColor: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        gap: 0.75,
        ...(active
          ? {
              bgcolor: color,
              borderColor: color,
              '&:hover': { bgcolor: color, opacity: 0.9 },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: active ? whiteColor : color,
        }}
      />
      {label}
    </Button>
  );
}
